import { useEffect, useRef, useState } from 'react';
import { Camera, Loader2, Pencil } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { toastError } from '@/lib/toastError';
import { ResilientImage } from '@/components/ResilientImage';

interface Props {
  /** ID of the user whose avatar we are rendering */
  userId: string;
  /** Display name (used for initials fallback) */
  name?: string | null;
  /**
   * Optional pre-known avatar URL. If omitted, the component fetches
   * client_onboarding_profiles.avatar_url for userId.
   */
  avatarUrl?: string | null;
  /** Visual size */
  size?: 'sm' | 'md' | 'lg' | 'xl';
  /** When true, the user can tap the avatar to upload a new photo */
  editable?: boolean;
  /** Called after a successful upload with the new URL */
  onUploaded?: (url: string) => void;
  /** Optional click handler when not editable (e.g. open profile) */
  onClick?: () => void;
  /** Extra classes for the container */
  className?: string;
}

const SIZE_CLASSES: Record<NonNullable<Props['size']>, { wrap: string; text: string; icon: string; badge: string }> = {
  sm: { wrap: 'w-8 h-8',  text: 'text-xs',  icon: 'w-3 h-3',     badge: 'w-3 h-3' },
  md: { wrap: 'w-12 h-12', text: 'text-sm',  icon: 'w-4 h-4',     badge: 'w-4 h-4' },
  lg: { wrap: 'w-20 h-20', text: 'text-2xl', icon: 'w-5 h-5',     badge: 'w-6 h-6' },
  xl: { wrap: 'w-28 h-28', text: 'text-4xl', icon: 'w-7 h-7',     badge: 'w-8 h-8' },
};

/** Get initials from a name like "Joel Vazquez Lopez" → "JV" */
function initials(name?: string | null): string {
  if (!name) return '?';
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return parts[0][0].toUpperCase();
  return (parts[0][0] + parts[1][0]).toUpperCase();
}

/** Deterministic color from name so each user has a stable color */
function colorForName(name?: string | null): string {
  if (!name) return 'hsl(0, 0%, 40%)';
  let hash = 0;
  for (let i = 0; i < name.length; i++) hash = (hash << 5) - hash + name.charCodeAt(i);
  const hue = Math.abs(hash) % 360;
  return `hsl(${hue}, 50%, 45%)`;
}

/**
 * Compress a File to a JPEG of max dimension 512px and ~80% quality.
 * Avatars don't need to be HD, and small files mean faster uploads on
 * spotty gym connections.
 */
async function compressImage(file: File, maxDim = 512, quality = 0.85): Promise<Blob> {
  const dataUrl = await new Promise<string>((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result as string);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });
  const img = await new Promise<HTMLImageElement>((resolve, reject) => {
    const i = new Image();
    i.onload = () => resolve(i);
    i.onerror = reject;
    i.src = dataUrl;
  });
  let { width, height } = img;
  if (width > maxDim || height > maxDim) {
    if (width > height) {
      height = Math.round(height * (maxDim / width));
      width = maxDim;
    } else {
      width = Math.round(width * (maxDim / height));
      height = maxDim;
    }
  }
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas not supported');
  ctx.drawImage(img, 0, 0, width, height);
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob(
      (b) => (b ? resolve(b) : reject(new Error('toBlob failed'))),
      'image/jpeg',
      quality,
    );
  });
}

export function UserAvatar({
  userId,
  name,
  avatarUrl,
  size = 'md',
  editable = false,
  onUploaded,
  onClick,
  className = '',
}: Props) {
  const [resolvedUrl, setResolvedUrl] = useState<string | null>(avatarUrl ?? null);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const cls = SIZE_CLASSES[size];

  // Fetch the avatar_url from the DB if not provided. La función se
  // declara afuera del useEffect para reutilizarla en el listener
  // de reconexión — si el avatar falló offline, al recuperar red
  // queremos refetchear la URL en caso de que no la tuviéramos
  // todavía cacheada.
  const fetchAvatarUrl = () => {
    if (avatarUrl !== undefined) {
      setResolvedUrl(avatarUrl);
      return;
    }
    if (!userId) return;
    supabase
      .from('client_onboarding_profiles')
      .select('avatar_url')
      .eq('user_id', userId)
      .maybeSingle()
      .then(({ data }) => {
        setResolvedUrl(data?.avatar_url || null);
      });
  };

  useEffect(() => {
    fetchAvatarUrl();
  }, [userId, avatarUrl]);

  // Si la red estaba caída cuando el usuario entró a la app, la
  // query de avatar_url pudo haber fallado o devuelto cache vacío.
  // Al recuperar conexión, refetcheamos para que la foto aparezca
  // sin que el usuario tenga que cerrar y abrir la app.
  useEffect(() => {
    const handler = () => fetchAvatarUrl();
    window.addEventListener('iftmx:reconnected', handler);
    return () => window.removeEventListener('iftmx:reconnected', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId, avatarUrl]);

  const handleFile = async (file: File) => {
    if (!file.type.startsWith('image/')) {
      toast.error('Solo imágenes (JPG, PNG, WEBP)');
      return;
    }

    // Defensa #1: userId vacío. Sin esto, path seria "undefined/avatar.jpg"
    // y la policy RLS rechazaria silenciosamente. Mejor avisar.
    if (!userId) {
      toast.error('No se pudo identificar tu sesión. Recarga la app e inicia sesión otra vez.');
      return;
    }

    // Defensa #2: confirmar que la sesion del browser coincide con userId.
    // Si la sesion expiro o el componente recibio userId de otro usuario,
    // RLS rechazara igual; aqui lo detectamos antes y damos un mensaje claro.
    const { data: authData } = await supabase.auth.getUser();
    const sessionUid = authData?.user?.id;
    if (!sessionUid) {
      toast.error('Tu sesión expiró. Vuelve a iniciar sesión y reintenta.');
      return;
    }
    if (sessionUid !== userId) {
      toast.error('No puedes subir la foto de perfil de otra cuenta.');
      return;
    }

    setUploading(true);
    try {
      const compressed = await compressImage(file);
      const path = `${userId}/avatar.jpg`;
      const { error: upErr } = await supabase.storage
        .from('avatars')
        .upload(path, compressed, {
          upsert: true,
          contentType: 'image/jpeg',
          cacheControl: '0', // immediate refresh after change
        });
      if (upErr) throw upErr;

      const { data: pub } = supabase.storage.from('avatars').getPublicUrl(path);
      // Cache-bust so the new image appears immediately
      const finalUrl = `${pub.publicUrl}?v=${Date.now()}`;

      const { error: dbErr } = await supabase
        .from('client_onboarding_profiles')
        .update({ avatar_url: finalUrl })
        .eq('user_id', userId);
      if (dbErr) throw dbErr;

      setResolvedUrl(finalUrl);
      onUploaded?.(finalUrl);
      toast.success('Foto de perfil actualizada');
    } catch (e: any) {
      // Mensajes especificos por tipo de error. Para errores de RLS,
      // explicar al usuario que vuelva a iniciar sesion porque
      // "reintentar" no soluciona nada (no es temporal).
      const msg = String(e?.message || '');
      const isRlsError = /row-level security|row level security|policy/i.test(msg);
      const isAuthError = /jwt|expired|auth/i.test(msg);
      const friendly = (isRlsError || isAuthError)
        ? 'Parece que tu sesión expiró o tienes un problema de permisos. Cierra sesión, vuelve a entrar y reintenta. Si sigue fallando, abre un ticket de soporte.'
        : 'Error al subir la foto de perfil: ' + (e?.message || 'intenta de nuevo');
      toastError(friendly, {
        section: 'avatar-upload',
        extra: { userId, sessionUid, errorMessage: msg, stack: e?.stack },
      });
    } finally {
      setUploading(false);
    }
  };

  const handleClick = () => {
    if (editable && !uploading) {
      fileInputRef.current?.click();
    } else if (onClick) {
      onClick();
    }
  };

  const showInitials = !resolvedUrl;
  const interactive = editable || !!onClick;
  const bg = colorForName(name);

  return (
    <div className={`relative inline-block ${className}`}>
      <button
        type="button"
        onClick={handleClick}
        disabled={!interactive && !uploading}
        className={`
          ${cls.wrap}
          rounded-full overflow-hidden
          flex items-center justify-center
          ${interactive ? 'cursor-pointer hover:ring-2 hover:ring-accent transition-all' : 'cursor-default'}
          ${showInitials ? 'text-white font-bold' : ''}
        `}
        style={showInitials ? { backgroundColor: bg } : undefined}
        aria-label={editable ? 'Cambiar foto de perfil' : `Foto de ${name || 'usuario'}`}
      >
        {uploading ? (
          <Loader2 className={`${cls.icon} animate-spin text-white`} />
        ) : resolvedUrl ? (
          // ResilientImage reintenta cargar al volver online y muestra
          // un indicador de "sin conexión" si la imagen fallo offline.
          // El fallback son las iniciales con el color determinístico,
          // que mantiene el mismo look visual de cuando no hay foto.
          <ResilientImage
            src={resolvedUrl}
            alt={name || 'Usuario'}
            className="w-full h-full object-cover"
            referrerPolicy="no-referrer"
            fallback={
              <span
                className={`${cls.wrap} flex items-center justify-center text-white font-bold ${cls.text}`}
                style={{ backgroundColor: bg }}
              >
                {initials(name)}
              </span>
            }
          />
        ) : (
          <span className={cls.text}>{initials(name)}</span>
        )}
      </button>

      {/* Edit badge — only on own editable avatar */}
      {editable && !uploading && (
        <span
          className={`
            absolute -bottom-0.5 -right-0.5 rounded-full bg-accent text-accent-foreground
            ${cls.badge} flex items-center justify-center shadow-md ring-2 ring-background
            pointer-events-none
          `}
          aria-hidden="true"
        >
          {size === 'sm' ? <Pencil className="w-2 h-2" /> : <Camera className="w-3 h-3" />}
        </span>
      )}

      {/* Hidden file input — opens camera or gallery */}
      {editable && (
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          className="hidden"
          onChange={(e) => {
            const f = e.target.files?.[0];
            if (f) handleFile(f);
            e.target.value = '';
          }}
        />
      )}
    </div>
  );
}
